// ManiDineRestaurantPortal /src/CustomerReviewsPage.jsx — Updated: 2026-04-01 16:20 (America/Edmonton)
// FILE: CustomerReviewsPage.jsx
import React, {useRef, useEffect, useState} from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  limit,
  query,
  Timestamp,
  setDoc,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "./firebase";
import useRestaurantContext from "./hooks/useRestaurantContext";

const REVIEWS_FILTERS_STORAGE_KEY = "manidine_portal_reviews_filters_v1";

function pad2(n) {
  return String(n).padStart(2, "0");
}
function formatYMD(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}/${pad2(dt.getMonth() + 1)}/${pad2(dt.getDate())}`;
}
function defaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { startDate: formatYMD(start), endDate: formatYMD(end) };
}

function dateFromFirestoreish(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function firstNonEmptyString(values) {
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return "";
}

function extractDerivedReviewFromOrder(order, restaurantName = "") {
  const restaurantRating =
    order?.customerRating?.restaurant ||
    order?.ratings?.restaurant ||
    order?.review?.restaurant ||
    order?.restaurantReview ||
    null;

  const rating = Number(
    restaurantRating?.rating ??
      order?.restaurantRating ??
      order?.rating ??
      NaN
  );

  if (!Number.isFinite(rating) || rating <= 0) {
    return null;
  }

  const replyText = firstNonEmptyString([
    restaurantRating?.replyText,
    restaurantRating?.reply,
    order?.restaurantReplyText,
    order?.replyText,
  ]);

  const comment = firstNonEmptyString([
    restaurantRating?.comment,
    restaurantRating?.text,
    restaurantRating?.review,
    restaurantRating?.body,
    order?.restaurantReviewComment,
    order?.reviewComment,
    order?.comment,
    order?.reviewText,
  ]);

  const createdAt =
    dateFromFirestoreish(restaurantRating?.createdAt) ||
    dateFromFirestoreish(restaurantRating?.updatedAt) ||
    dateFromFirestoreish(order?.completedAt) ||
    dateFromFirestoreish(order?.deliveredAt) ||
    dateFromFirestoreish(order?.updatedAt) ||
    dateFromFirestoreish(order?.createdAt);

  return {
    id: `derived-${order.id}`,
    orderId: order.id,
    sourceOrderId: order.id,
    rating,
    comment,
    replyText,
    replyStatus: replyText ? "replied" : "not_replied",
    customerId:
      order?.userId ||
      order?.customerId ||
      order?.customerUid ||
      order?.uid ||
      "",
    customerName:
      order?.customerName ||
      order?.userName ||
      order?.name ||
      "",
    restaurant:
      restaurantName ||
      order?.restaurantName ||
      "",
    createdAt: createdAt || new Date(0),
    derived: true,
  };
}

function reviewMatchesFilters(review, filters, startDate, endDate) {
  if (!review) return false;

  const ratingFilter = Number(filters?.starRating);
  if (!Number.isNaN(ratingFilter) && filters?.starRating && filters.starRating !== "all") {
    if (Number(review.rating) !== ratingFilter) {
      return false;
    }
  }

  const normalizedReplyStatus = String(review.replyStatus || "")
    .trim()
    .toLowerCase();

  if (filters?.replyStatus === "replied" && normalizedReplyStatus !== "replied") {
    return false;
  }

  if (
    filters?.replyStatus === "not_replied" &&
    normalizedReplyStatus !== "not_replied"
  ) {
    return false;
  }

  const createdAt = dateFromFirestoreish(review.createdAt);
  if (startDate && (!createdAt || createdAt < startDate)) {
    return false;
  }
  if (endDate && (!createdAt || createdAt > endDate)) {
    return false;
  }

  return true;
}


const navItems = [
  "Home",
  "Stores",
  "Orders",
  "Performance",
  "Customers",
  "Customer insights",
  "Reviews",
  "Reports",
  "Marketing",
  "Menu",
  "Payments",
  "Users",
];

const replyStatusOptions = [
  { label: "All", value: "all" },
  { label: "Replied", value: "replied" },
  { label: "Not replied", value: "not_replied" },
];

const Sidebar = () => (
  <aside className="reviews-sidebar">
    <div className="sidebar-logo">Uber Eats Manager</div>
    <nav className="sidebar-nav">
      {navItems.map((item) => {
        const isActive = item === "Reviews";
        return (
          <div
            key={item}
            className={`sidebar-item ${isActive ? "active" : ""}`}
          >
            {isActive && <span className="sidebar-highlight" aria-hidden />}
            <span className="sidebar-icon">•</span>
            <span>{item}</span>
          </div>
        );
      })}
    </nav>
  </aside>
);

const TopBar = () => (
  <div className="reviews-topbar">
    <button type="button" className="ghost-button">
      Help
    </button>
    <button type="button" className="ghost-button">
      Log out
    </button>
  </div>
);

const FiltersBar = ({ filters, onFilterChange, restaurantId }) => (
  <section className="filters-section">
    <div className="filters-header">
      <h2>Customer reviews</h2>
    </div>
    <div className="filters-row">
      <div className="filter">
        <label>Restaurant</label>
        <select
          value={filters.restaurantId}
          onChange={(e) =>
            onFilterChange({ ...filters, restaurantId: e.target.value })
          }
          disabled={!restaurantId}
        >
          <option value={restaurantId || ""}>Current restaurant</option>
        </select>
      </div>
      <div className="filter">
        <label>Date range</label>
        <select
          value={filters.dateRange || "last30"}
          onChange={(e) => onFilterChange({ ...filters, dateRange: e.target.value })}
        >
          <option value="last7">Last 7 days</option>
          <option value="last30">Last 30 days</option>
          <option value="last90">Last 90 days</option>
          <option value="all">All time</option>
        </select>
      </div>
      <div className="filter">
        <label>Star rating</label>
        <select
          value={filters.starRating}
          onChange={(e) =>
            onFilterChange({ ...filters, starRating: e.target.value })
          }
        >
          <option value="">All ratings</option>
          {[5, 4, 3, 2, 1].map((rating) => (
            <option key={rating} value={rating}>
              {rating} star{rating > 1 ? "s" : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="filter">
        <label>Review tags</label>
        <select defaultValue="All tags">
          <option>All tags</option>
          <option>Delivery</option>
          <option>Food quality</option>
          <option>Packaging</option>
        </select>
      </div>
      <div className="filter">
        <label>Reply status</label>
        <select
          value={filters.replyStatus}
          onChange={(e) =>
            onFilterChange({ ...filters, replyStatus: e.target.value })
          }
        >
          {replyStatusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="manage-button-wrapper">
        <button type="button" className="manage-button">
          Manage auto replies
        </button>
      </div>
    </div>
  </section>
);

const StarRating = ({ rating }) => {
  const stars = Array.from({ length: 5 }, (_, index) =>
    index < rating ? "★" : "☆"
  );

  return <div className="star-rating">{stars.join(" ")}</div>;
};

const ReviewItem = ({
  review,
  isReplying,
  draftReply,
  onStartReply,
  onDraftChange,
  onCancelReply,
  onSendReply,
  replySaving,
}) => {
  const displayName =
  review.name ||
  review.customerName ||
  (review.customerId ? `Customer ${String(review.customerId).slice(0, 4)}` : "Customer");

const initials = String(displayName)
  .split(" ")
  .filter(Boolean)
  .map((n) => n[0])
  .join("")
  .slice(0, 2)
  .toUpperCase();

  const bodyText =
  (review.comment ?? review.body ?? review.text ?? "").toString().trim() ||
  "(No comment)";

  const normalizedReplyStatus = (() => {
    const raw = String(review.replyStatus || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    if (raw === "replied" || raw === "not_replied") {
      return raw;
    }

    const hasReplyText = String(review.reply || review.replyText || "").trim().length > 0;
    return hasReplyText ? "replied" : "not_replied";
  })();

  const isReplied = normalizedReplyStatus === "replied";
  const replyStatusLabel = isReplied ? "Replied" : "Not replied";

  return (
    <div className="review-row">
      <div className="avatar" aria-hidden="true">
        {initials}
      </div>
      <div className="review-meta">
        <div className="review-name">{displayName}</div>
        <div className="review-subtext">
          {review.orderMeta || (review.orderId ? `Order: ${String(review.orderId).slice(0, 8)}…` : `Order: ${String(review.id).slice(0, 8)}…`) }
        </div>
        <StarRating rating={review.rating} />
        <div className="review-subtext">{review.restaurant}</div>
      </div>
      <div className="review-body">
        <div className="review-text">{bodyText}</div>
        {isReplied && (review.reply || review.replyText) && (
          <div className="review-reply">
            <strong>Your reply:</strong> {review.reply || review.replyText}
          </div>
        )}
        {isReplying && (
          <div className="reply-editor">
            <textarea
              value={draftReply}
              placeholder="Type your reply..."
              onChange={(e) => onDraftChange(e.target.value)}
            />
            <div className="reply-actions">
              <button type="button" className="ghost-button" onClick={onCancelReply}>
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={onSendReply}
                disabled={!draftReply.trim() || replySaving}
              >
                {replySaving ? "Sending…" : "Send reply"}
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="review-actions">
        <button
          type="button"
          className="reply-button"
          onClick={() => onStartReply(review.id)}
        >
          {isReplied ? "Edit reply" : "Reply"}
        </button>
        <div className={`reply-status ${isReplied ? "sent" : ""}`}>
          {replyStatusLabel}
        </div>
      </div>
    </div>
  );
};

const CustomerReviewsPage = ({ embedded = false }) => {
  const {
    restaurantId,
    restaurant,
    loading: restaurantLoading,
    ratingAvg: ctxRatingAvg,
    ratingCount: ctxRatingCount,
  } = useRestaurantContext() || {};

  // ⭐ Local aggregate fallback (in case context isn’t updated)
  const [ratingAvg, setRatingAvg] = useState(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [customerMap, setCustomerMap] = useState({}); // uid -> { fullName, name, email }
  const [derivedReviews, setDerivedReviews] = useState([]);
  const [usingDerived, setUsingDerived] = useState(false);
  const initRef = useRef(false);
  const [filters, setFilters] = useState(() => ({
    restaurantId: restaurantId || "",
    dateRange: "last30", // last30 | last7 | last90 | all
    starRating: "all",
    tags: "all",
    replyStatus: "all",
  }));
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [replyingReviewId, setReplyingReviewId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replySaving, setReplySaving] = useState(false);
const formatDateTime = (value) => {
  if (!value) return "";
  try {
    // Firestore Timestamp
    const d =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
};

  useEffect(() => {
    // ✅ Only initialize once (prevents date reset on tab switch)
    if (initRef.current) return;

    setFilters((prev) => ({
      ...prev,
      // keep your existing defaults
    }));

    initRef.current = true;
  }, [restaurantId]);

  // 📅 Date range -> start/end timestamps (used in Firestore query)
  useEffect(() => {
    const key = filters.dateRange || "last30";
    if (key === "all") {
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const end = new Date();
    const start = new Date(end.getTime());
    const days = key === "last7" ? 7 : key === "last90" ? 90 : 30;
    start.setDate(start.getDate() - days);

    // Firestore range queries are inclusive; endDate = now
    setStartDate(start);
    setEndDate(end);
  }, [filters.dateRange]);



  // ⭐ Read aggregate rating directly from restaurant doc (restaurants/{id}.ratings)
  useEffect(() => {
    if (!restaurantId) {
      setRatingAvg(null);
      setRatingCount(0);
      return () => {};
    }
    const ref = doc(db, "restaurants", restaurantId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        const r = data.ratings || {};
        const count = Number(r.count) || 0;
        const sum = Number(r.sum) || 0;
        setRatingCount(count);
        setRatingAvg(count > 0 ? sum / count : null);
      },
      (err) => {
        console.error("[CustomerReviewsPage] restaurant aggregate listener error:", err);
      }
    );
    return () => unsub();
  }, [restaurantId]);


  useEffect(() => {
    const { restaurantId, starRating, replyStatus } = filters;
    if (restaurantLoading) {
      setLoading(true);
      return () => {};
    }
    if (!restaurantId) {
      setReviews([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const baseRef = collection(db, "restaurants", restaurantId, "reviews");
    const constraints = [
      orderBy("createdAt", "desc"),
    ];

    if (starRating) {
      const ratingNumber = Number(starRating);
      if (!Number.isNaN(ratingNumber)) {
        constraints.push(where("rating", "==", ratingNumber));
      }
    }

    if (replyStatus && replyStatus !== "all") {
      if (replyStatus === "not_replied") {
        constraints.push(where("replyStatus", "==", "not_replied"));
      } else if (replyStatus === "replied") {
        constraints.push(where("replyStatus", "==", "replied"));
      }
    }

    if (startDate && endDate) {
      constraints.push(
        where("createdAt", ">=", Timestamp.fromDate(startDate)),
        where("createdAt", "<=", Timestamp.fromDate(endDate))
      );
    }

    constraints.push(limit(200));

    const q = query(baseRef, ...constraints);
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setReviews(data);
        // If restaurant reviews subcollection is empty, fall back to deriving from orders
        setUsingDerived(data.length === 0);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading reviews:", error);
        setUsingDerived(true);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [filters, endDate, startDate, restaurantLoading]);

  useEffect(() => {
    let cancelled = false;

    const loadDerivedReviews = async () => {
      if (!usingDerived || !filters.restaurantId) {
        setDerivedReviews([]);
        return;
      }

      try {
        const ordersRef = collection(db, "orders");
        const derivedQuery = query(
          ordersRef,
          where("restaurantId", "==", filters.restaurantId),
          limit(300)
        );
        const snapshot = await getDocs(derivedQuery);

        const rows = snapshot.docs
          .map((docSnap) =>
            extractDerivedReviewFromOrder(
              { id: docSnap.id, ...docSnap.data() },
              restaurant?.restaurantName || ""
            )
          )
          .filter(Boolean)
          .filter((review) => reviewMatchesFilters(review, filters, startDate, endDate))
          .sort((a, b) => {
            const aTime = dateFromFirestoreish(a?.createdAt)?.getTime() || 0;
            const bTime = dateFromFirestoreish(b?.createdAt)?.getTime() || 0;
            return bTime - aTime;
          });

        if (!cancelled) {
          setDerivedReviews(rows);
        }
      } catch (error) {
        console.error("Error loading derived reviews:", error);
        if (!cancelled) {
          setDerivedReviews([]);
        }
      }
    };

    loadDerivedReviews();

    return () => {
      cancelled = true;
    };
  }, [filters, restaurant?.restaurantName, startDate, endDate, usingDerived]);

const reviewSourceForNames = usingDerived ? derivedReviews : reviews;

// ✅ Enrich reviews with customer display names (so we don't show "Unknown customer")
useEffect(() => {
  let cancelled = false;

  const loadCustomers = async () => {
    const ids = Array.from(
      new Set(
        (reviewSourceForNames || [])
          .map((r) => r.customerId || r.userId || r.customerUid || r.uid)
          .filter(Boolean)
      )
    );

    if (ids.length === 0) {
      setCustomerMap({});
      return;
    }

    try {
      const pairs = await Promise.all(
        ids.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "users", id));
            if (!snap.exists()) return [id, null];
            const data = snap.data() || {};
            return [id, { fullName: data.fullName, name: data.name, email: data.email }];
          } catch (e) {
            return [id, null];
          }
        })
      );

      if (!cancelled) {
        const next = {};
        for (const [id, info] of pairs) {
          if (info) next[id] = info;
        }
        setCustomerMap(next);
      }
    } catch (e) {
      if (!cancelled) setCustomerMap({});
    }
  };

  loadCustomers();

  return () => {
    cancelled = true;
  };
}, [derivedReviews, reviewSourceForNames, reviews, usingDerived]);
  const handleStartReply = (review) => {
    setReplyingReviewId(review.id);
    setReplyDraft(review.replyText || "");
  };

  const handleCancelReply = () => {
    setReplyingReviewId(null);
    setReplyDraft("");
    setReplySaving(false);
  };

  const handleSendReply = async (review) => {
    const trimmed = replyDraft.trim();
    if (!trimmed) return;
    if (!filters.restaurantId) return;

    setReplySaving(true);
    try {
      if (usingDerived) {
        const reviewDocId = review.orderId || review.sourceOrderId || review.id;
        const reviewRef = doc(
          db,
          "restaurants",
          filters.restaurantId,
          "reviews",
          reviewDocId
        );

        await setDoc(
          reviewRef,
          {
            orderId: review.orderId || review.sourceOrderId || "",
            customerId:
              review.customerId || review.userId || review.customerUid || review.uid || "",
            customerName: review.customerName || review.name || "",
            rating: Number(review.rating || 0),
            comment: (review.comment ?? review.body ?? review.text ?? "").toString(),
            restaurant:
              review.restaurant ||
              restaurant?.restaurantName ||
              "",
            createdAt:
              review.createdAt && typeof review.createdAt?.toDate === "function"
                ? review.createdAt
                : review.createdAt instanceof Date
                ? Timestamp.fromDate(review.createdAt)
                : serverTimestamp(),
            replyText: trimmed,
            replyStatus: "replied",
            replyAt: serverTimestamp(),
            source: "order_fallback",
          },
          { merge: true }
        );

        const sourceOrderId = review.orderId || review.sourceOrderId;
        if (sourceOrderId) {
          try {
            const orderRef = doc(db, "orders", sourceOrderId);
            await updateDoc(orderRef, {
              "customerRating.restaurant.replyText": trimmed,
              "customerRating.restaurant.replyStatus": "replied",
              "customerRating.restaurant.replyAt": serverTimestamp(),
            });
          } catch (orderReplyError) {
            console.error("Error syncing reply to order review:", orderReplyError);
          }
        }
      } else {
        const ref = doc(
          db,
          "restaurants",
          filters.restaurantId,
          "reviews",
          review.id
        );
        await updateDoc(ref, {
          replyText: trimmed,
          replyStatus: "replied",
          replyAt: serverTimestamp(),
        });
      }

      setReplyingReviewId(null);
      setReplyDraft("");
    } catch (err) {
      console.error("Error sending reply:", err);
    } finally {
      setReplySaving(false);
    }
  };

  const displayReviews = usingDerived ? derivedReviews : reviews;

  const panel = (
    <div className="content-card reviews-panel">
      <FiltersBar
        filters={filters}
        onFilterChange={setFilters}
        restaurantId={restaurantId}
      />
      {/* ⭐ Restaurant rating (from restaurants/{id}.ratings aggregate) */}
      <div className="rating-summary">
        <div className="rating-summary-left">
          <div className="rating-summary-label">Overall rating</div>
          <div className="rating-summary-value">
            <span className="star">★</span>
            {typeof ratingAvg === "number" && (ratingCount || 0) > 0 ? ratingAvg.toFixed(1) : "--"}
            <span className="rating-summary-count"> ({ratingCount || 0})</span>
          </div>
        </div>
      </div>

      {restaurantLoading ? (
        <div className="empty-state">Loading restaurant…</div>
      ) : !filters.restaurantId ? (
        <div className="empty-state">
          No restaurant found. Please sign in again.
        </div>
      ) : loading ? (
        <div className="empty-state">Loading reviews...</div>
      ) : displayReviews.length === 0 ? (
        <div className="empty-state">No reviews found yet.</div>
      ) : (
        <div className="reviews-list">
          {displayReviews.map((review) => (
            <ReviewItem
              key={review.id}
              review={{
            ...review,
            name: (() => {
              const cid = review.customerId || review.userId || review.customerUid || review.uid;
              const info = cid ? customerMap[cid] : null;
              return (
                review.customerName ||
                info?.fullName ||
                info?.name ||
                info?.email ||
                "Unknown customer"
              );
            })(),
            orderMeta: (() => {
              const id = review.orderId || review.id;
              const when = formatDateTime(review.createdAt);
              const shortId = id ? `${String(id).slice(0, 8)}…` : "";
              return when ? `Order: ${shortId} • ${when}` : `Order: ${shortId}`;
            })(),
            reply: review.replyText,
          }}
              isReplying={replyingReviewId === review.id}
              draftReply={replyingReviewId === review.id ? replyDraft : ""}
              onStartReply={() => handleStartReply(review)}
              onDraftChange={setReplyDraft}
              onCancelReply={handleCancelReply}
              onSendReply={() => handleSendReply(review)}
              replySaving={replySaving}
            />
          ))}
        </div>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="reviews-embedded">
        <style>{styles}</style>
        {panel}
      </div>
    );
  }

  return (
    <div className="reviews-page">
      <style>{styles}</style>
      <div className="reviews-shell">
        <Sidebar />
        <main className="reviews-content">
          <TopBar />
          {panel}
        </main>
      </div>
    </div>
  );
};

const styles = `
:root {
  font-family: "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #0f172a;
}
.reviews-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #e0f2df, #d1f1d7);
  padding: 32px;
  box-sizing: border-box;
}
.reviews-shell {
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  background: #fff;
  border-radius: 24px;
  overflow: hidden;
  min-height: calc(100vh - 64px);
  box-shadow:
    0 20px 45px rgba(15, 23, 42, 0.12),
    0 8px 20px rgba(15, 23, 42, 0.08);
}
.reviews-sidebar {
  width: 220px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  padding: 24px;
}
.reviews-shell .sidebar-logo {
  font-weight: 600;
  margin-bottom: 24px;
}
.reviews-shell .sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.reviews-shell .sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  position: relative;
  color: #475569;
  font-size: 0.95rem;
}
.reviews-shell .sidebar-item .sidebar-icon {
  font-size: 0.7rem;
  color: #94a3b8;
}
.reviews-shell .sidebar-item.active {
  background: #e2e8f0;
  color: #0f172a;
  font-weight: 600;
}
.reviews-shell .sidebar-highlight {
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 2px;
  background: #0f172a;
}
.reviews-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 32px;
  background: #f8fafc;
}
.reviews-topbar {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
}
.content-card {
  flex: 1;
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  box-shadow: inset 0 0 0 1px #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 32px;
}
.reviews-header h1 {
  font-size: 2rem;
  margin-bottom: 16px;
}
.reviews-tabs {
  display: flex;
  gap: 24px;
}
.tab {
  background: none;
  border: none;
  font-size: 1rem;
  color: #94a3b8;
  padding-bottom: 6px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}
.tab.active {
  color: #0f172a;
  font-weight: 600;
  border-bottom-color: #0f172a;
}
.filters-section h2 {
  margin-bottom: 8px;
  font-size: 1.25rem;
}

.rating-summary{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:14px 16px;
  margin: 10px 0 14px;
  border: 1px solid rgba(15,23,42,0.10);
  border-radius: 14px;
  background: rgba(255,255,255,0.85);
}
.rating-summary-label{
  font-size: 13px;
  font-weight: 700;
  color: rgba(15,23,42,0.75);
}
.rating-summary-value{
  margin-top: 6px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  display:flex;
  align-items:center;
  gap: 8px;
}
.rating-summary-value .star{
  color: #f59e0b;
  font-size: 18px;
  line-height: 1;
}
.rating-summary-count{
  font-size: 14px;
  font-weight: 700;
  color: rgba(15,23,42,0.55);
}
.filters-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  align-items: end;
}
.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter label {
  font-size: 0.85rem;
  color: #64748b;
}
.filter select {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.95rem;
  background: #fff;
}
.manage-button-wrapper {
  display: flex;
  justify-content: flex-end;
}
.manage-button {
  border: 1px solid #0f172a;
  border-radius: 999px;
  padding: 10px 18px;
  font-weight: 600;
  background: #0f172a;
  color: #fff;
}
.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.review-row {
  display: grid;
  grid-template-columns: 70px 200px 1fr 120px;
  gap: 16px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #f8fafc;
}
.avatar {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: #c7d2fe;
  color: #312e81;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.review-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.9rem;
}
.review-name {
  font-weight: 600;
  color: #0f172a;
}
.review-subtext {
  color: #94a3b8;
  font-size: 0.85rem;
}
.star-rating {
  font-size: 0.95rem;
  color: #f97316;
}
.review-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.review-text {
  background: #fff;
  border-radius: 12px;
  padding: 12px 14px;
  color: #0f172a;
  line-height: 1.4;
  border: 1px solid #e2e8f0;
}
.review-reply {
  font-size: 0.85rem;
  color: #475569;
  background: #e2e8f0;
  border-radius: 10px;
  padding: 10px;
}
.review-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}
.reply-button {
  border: none;
  background: #0f172a;
  color: #fff;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
}
.reply-status {
  font-size: 0.85rem;
  color: #94a3b8;
}
.reply-status.sent {
  color: #16a34a;
  font-weight: 600;
}
.reply-editor textarea {
  width: 100%;
  min-height: 80px;
  border-radius: 12px;
  border: 1px solid #cbd5f5;
  padding: 12px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 10px;
}
.reply-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.ghost-button {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  border-radius: 999px;
  padding: 8px 18px;
  font-weight: 500;
  cursor: pointer;
}
.primary-button {
  border: none;
  background: #0f172a;
  color: #fff;
  border-radius: 999px;
  padding: 8px 20px;
  font-weight: 600;
  cursor: pointer;
}
.empty-state {
  text-align: center;
  padding: 48px;
  color: #64748b;
  border: 2px dashed #e2e8f0;
  border-radius: 16px;
}
.reviews-embedded {
  width: 100%;
}
.reviews-embedded .content-card {
  width: 100%;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  padding: 24px;
}
.reviews-embedded .reviews-tabs {
  flex-wrap: wrap;
}
.reviews-panel {
  margin-top: 0;
}
@media (max-width: 1024px) {
  .reviews-shell {
    flex-direction: column;
  }
  .reviews-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }
  .reviews-content {
    padding: 24px;
  }
  .review-row {
    grid-template-columns: 70px 1fr;
  }
  .review-actions {
    align-items: flex-start;
  }
}
@media (max-width: 768px) {
  .reviews-page {
    padding: 16px;
  }
  .reviews-shell {
    min-height: auto;
  }
  .reviews-sidebar {
    display: none;
  }
  .content-card {
    padding: 20px;
  }
  .filters-row {
    grid-template-columns: 1fr;
  }
  .review-row {
    grid-template-columns: 60px 1fr;
  }
}
`;

export default CustomerReviewsPage;
